import { useMemo, useState } from 'react';

import styled from 'styled-components';
import {
	IHighlightContentDB,
	ITrendParams,
	ITrendingContentDB,
	ITrendingContentParams,
} from 'type/tracking';

import {
	useGetTrackingHighlights,
	useGetInfluencers,
	useGetTrendingContents,
} from 'hooks/queries/tracking';
import CoreImg from 'shared/CoreImg';
import CoreLoading from 'shared/CoreLoading';
import CoreSvg from 'shared/CoreSvg';
import CoreTooltip from 'shared/CoreTooltip';
import Icon from 'shared/icons';
import { ReportSection } from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

const CONTENT_TYPE_ICON: Record<string, JSX.Element> = {
	ig_feed: <Icon.Insta />,
	ig_video: <Icon.InstaReels />,
	yt_video: <Icon.Youtube />,
	yt_shorts: <Icon.YoutubeShorts />,
};

interface ITrendHighlights {
	code: string;
	trendParams?: ITrendParams;
	selectedDate?: string | null;
	onClearDate?: () => void;
}

const METRIC_LABEL_MAP: Record<string, string> = {
	VIEW: '조회 수',
	LIKE: '좋아요 수',
	COMMENT: '댓글 수',
	SHARE: '공유 수',
	ENGAGEMENT: '인게이지먼트',
};

const TRENDING_TOOLTIP = `선택한 지표 기준으로, 당일 증가량이 최근 평균 증가량의 2배 이상일 때 급상승 배지(⚡)가 표시됩니다. 데이터가 최소 3일 이상 쌓인 콘텐츠부터 집계됩니다.

인스타 피드: 인게이지먼트
인스타 릴스 · 유튜브 쇼츠 · 유튜브 롱폼: 조회수

* 공유 지표는 현재 급상승 기준에 포함되지 않습니다.`;

const formatDate = (dateStr: string) => {
	if (!dateStr) return '';
	const d = new Date(dateStr);
	if (isNaN(d.getTime())) return '';
	const yy = String(d.getFullYear()).slice(2);
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yy}.${mm}.${dd}`;
};

const TrendHighlights = ({ code, trendParams, selectedDate, onClearDate }: ITrendHighlights) => {
	const [activeTab, setActiveTab] = useState<'highlight' | 'trending'>('highlight');

	const { data: influencers } = useGetInfluencers(code);
	const influencerPkMap = useMemo(
		() => new Map(influencers?.map((i) => [i.influencerCode, i.accountCode]) ?? []),
		[influencers],
	);

	const metric = trendParams?.metric ?? 'VIEW';
	const highlightParams = trendParams
		? {
				metric: trendParams.metric,
				contentTypes: trendParams.contentTypes,
				limit: 10,
				...(selectedDate ? { date: selectedDate } : {}),
		  }
		: { metric: 'VIEW' as const, limit: 10 };

	const trendingParams: ITrendingContentParams = {
		metric: metric === 'ENGAGEMENT' ? undefined : (metric as 'VIEW' | 'LIKE' | 'COMMENT' | 'SHARE'),
		contentTypes: trendParams?.contentTypes,
		startDate: selectedDate ?? trendParams?.startDate,
		endDate: selectedDate ?? trendParams?.endDate,
		pageSize: 999,
	};

	const { data: highlights, isLoading: highlightsLoading } = useGetTrackingHighlights(
		code,
		highlightParams,
	);
	const { data: trendingResponse, isLoading: trendingLoading } = useGetTrendingContents(
		code,
		trendingParams,
	);

	const metricLabel = METRIC_LABEL_MAP[metric] ?? '조회 수';
	const trendingCount = trendingResponse?.totalCount ?? 0;

	const isLoading = activeTab === 'highlight' ? highlightsLoading : trendingLoading;

	if (isLoading) {
		return (
			<ReportSection _margin="40px 0 0 0">
				<Grid flex="center" _height="200px">
					<CoreLoading color_type="black" size_type="large" />
				</Grid>
			</ReportSection>
		);
	}

	return (
		<ReportSection _margin="40px 0 0 0">
			<TabRow>
				<Tab $active={activeTab === 'highlight'} onClick={() => setActiveTab('highlight')}>
					하이라이트 Top 10 <MetricRef>({metricLabel} 기준)</MetricRef>
					<InfoIconWrapper>
						<InfoIcon>ⓘ</InfoIcon>
						<Tooltip>선택한 지표 기준 상위 10개 콘텐츠입니다.</Tooltip>
					</InfoIconWrapper>
				</Tab>
				<Tab $active={activeTab === 'trending'} onClick={() => setActiveTab('trending')}>
					급상승 콘텐츠 <MetricRef>({trendingCount}개)</MetricRef>
					<InfoIconWrapper>
						<InfoIcon>ⓘ</InfoIcon>
						<Tooltip $wide>{TRENDING_TOOLTIP}</Tooltip>
					</InfoIconWrapper>
				</Tab>
			</TabRow>

			{selectedDate && (
				<DateChipRow>
					<DateChip>
						{selectedDate}
						<DateChipClear onClick={onClearDate} aria-label="날짜 필터 해제">
							×
						</DateChipClear>
					</DateChip>
				</DateChipRow>
			)}

			{activeTab === 'highlight' ? (
				!highlights?.length ? (
					<EmptyMessage>
						{selectedDate
							? '해당 날짜에 집계된 콘텐츠가 없습니다.'
							: '하이라이트 콘텐츠가 없습니다.'}
					</EmptyMessage>
				) : (
					<ScrollContainer>
						{highlights.map((item: IHighlightContentDB) => (
							<HighlightCard
								key={`${item.contentLink}-${item.rank}`}
								onClick={() =>
									window.open(item.contentLink, '_blank', 'noopener,noreferrer')
								}
							>
								<CoreTooltip direction="down" text="계정 리포트 바로가기">
									<CreatorRow
										onClick={(e) => {
											e.stopPropagation();
											const pk =
												influencerPkMap.get(item.influencerCode) ?? item.username;
											window.open(
												`https://app.featuring.co/report/${(item.platformType ?? '').toLowerCase()}/${pk}`,
												'_blank',
												'noopener,noreferrer',
											);
										}}
									>
										<CreatorAvatar>
											<CoreImg
												src={item.profileImageUrl}
												alt=""
												_width={24}
												_height={24}
												error_src="https://featuring-score-web.s3.amazonaws.com/static/images/score/v3/common/cmmn-default-image.png"
												lazy
											/>
										</CreatorAvatar>
										<CreatorName>{item.username}</CreatorName>
									</CreatorRow>
								</CoreTooltip>
								<ThumbnailWrapper>
									<RankBadge $isTop={item.rank <= 3}>{item.rank}위</RankBadge>
									<CoreImg
										src={item.thumbnailUrl}
										alt=""
										_width={200}
										_height={200}
										error_src="https://featuring-score-web.s3.amazonaws.com/static/images/score/v3/common/cmmn-default-image.png"
										lazy
									/>
								</ThumbnailWrapper>
								<CardInfo>
									{CONTENT_TYPE_ICON[item.contentType] && (
										<CoreSvg _size="16px">{CONTENT_TYPE_ICON[item.contentType]}</CoreSvg>
									)}
									<CoreTooltip
										direction="up"
										text={item.contentTitle ?? item.contentDescription ?? ''}
									>
										<CardDescription>
											{item.contentTitle ?? item.contentDescription}
										</CardDescription>
									</CoreTooltip>
									<CardDate>{formatDate(item.uploadDate)}</CardDate>
								</CardInfo>
							</HighlightCard>
						))}
					</ScrollContainer>
				)
			) : !trendingResponse?.contents?.length ? (
				<EmptyMessage>급상승 콘텐츠가 없습니다.</EmptyMessage>
			) : (
				<ScrollContainer>
					{trendingResponse.contents.map((item: ITrendingContentDB, idx: number) => (
						<HighlightCard
							key={`${item.contentLink}-${idx}`}
							onClick={() =>
								window.open(item.contentLink, '_blank', 'noopener,noreferrer')
							}
						>
							<CoreTooltip direction="down" text="계정 리포트 바로가기">
								<CreatorRow
									onClick={(e) => {
										e.stopPropagation();
										const pk =
											influencerPkMap.get(item.influencerCode) ?? item.username;
										window.open(
											`https://app.featuring.co/report/${(item.platformType ?? '').toLowerCase()}/${pk}`,
											'_blank',
											'noopener,noreferrer',
										);
									}}
								>
									<CreatorAvatar>
										<CoreImg
											src={item.profileImageUrl}
											alt=""
											_width={24}
											_height={24}
											error_src="https://featuring-score-web.s3.amazonaws.com/static/images/score/v3/common/cmmn-default-image.png"
											lazy
										/>
									</CreatorAvatar>
									<CreatorName>{item.username}</CreatorName>
								</CreatorRow>
							</CoreTooltip>
							<ThumbnailWrapper>
								<CoreImg
									src={item.thumbnailUrl}
									alt=""
									_width={200}
									_height={200}
									error_src="https://featuring-score-web.s3.amazonaws.com/static/images/score/v3/common/cmmn-default-image.png"
									lazy
								/>
							</ThumbnailWrapper>
							<CardInfo>
								{CONTENT_TYPE_ICON[item.contentType] && (
									<CoreSvg _size="16px">{CONTENT_TYPE_ICON[item.contentType]}</CoreSvg>
								)}
								<CoreTooltip
									direction="up"
									text={item.contentTitle ?? item.contentDescription ?? ''}
								>
									<CardDescription>
										{item.contentTitle ?? item.contentDescription}
									</CardDescription>
								</CoreTooltip>
								<CardDate>{formatDate(item.uploadDate)}</CardDate>
							</CardInfo>
						</HighlightCard>
					))}
				</ScrollContainer>
			)}
		</ReportSection>
	);
};

const TabRow = styled.div`
	display: flex;
	gap: 0;
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	margin-bottom: 16px;
`;

const Tab = styled.div<{ $active?: boolean }>`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 12px 16px;
	cursor: pointer;
	border-bottom: 2px solid
		${({ $active, theme }) => ($active ? theme.colors.uplus.primary50_key : 'transparent')};
	${({ theme }) => theme.typo.body.p};
	font-weight: ${({ $active }) => ($active ? 700 : 400)};
	color: ${({ $active, theme }) =>
		$active ? theme.colors.gray.gray80_text : theme.colors.gray.gray50};
	transition: color 0.15s, border-bottom-color 0.15s;
	white-space: nowrap;
`;

const MetricRef = styled.span`
	${({ theme }) => theme.typo.body.p};
	color: ${({ theme }) => theme.colors.gray.gray50};
	font-weight: 400;
`;

const InfoIconWrapper = styled.div`
	position: relative;
	display: inline-flex;

	&:hover > div {
		opacity: 1;
		visibility: visible;
	}
`;

const InfoIcon = styled.span`
	${({ theme }) => theme.typo.body.p};
	color: ${({ theme }) => theme.colors.gray.gray30};
	cursor: pointer;
`;

const Tooltip = styled.div<{ $wide?: boolean }>`
	position: absolute;
	top: 100%;
	left: 50%;
	transform: translateX(-50%);
	margin-top: 6px;
	padding: 8px 12px;
	background-color: ${({ theme }) => theme.colors.gray.gray70};
	color: #fff;
	${({ theme }) => theme.typo.body.span};
	border-radius: 6px;
	white-space: ${({ $wide }) => ($wide ? 'pre-line' : 'nowrap')};
	width: ${({ $wide }) => ($wide ? '280px' : 'auto')};
	opacity: 0;
	visibility: hidden;
	transition: opacity 0.15s;
	z-index: 10;
	pointer-events: none;
`;

const DateChipRow = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 12px;
`;

const DateChip = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 2px 8px;
	border-radius: 12px;
	background-color: ${({ theme }) => theme.colors.uplus.primary50_key};
	color: #fff;
	${({ theme }) => theme.typo.body.span};
	font-size: 12px;
`;

const DateChipClear = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	color: #fff;
	font-size: 14px;
	line-height: 1;
	cursor: pointer;
	padding: 0;
	margin-left: 2px;

	&:hover {
		opacity: 0.8;
	}
`;

const ScrollContainer = styled.div`
	display: flex;
	gap: 16px;
	overflow-x: auto;
	padding-bottom: 8px;

	&::-webkit-scrollbar {
		height: 6px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: ${({ theme }) => theme.colors.gray.gray30};
		border-radius: 3px;
	}

	&::-webkit-scrollbar-track {
		background-color: transparent;
	}
`;

const HighlightCard = styled.div`
	width: 200px;
	min-width: 200px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-radius: 12px;
	overflow: hidden;
	cursor: pointer;
	transition: box-shadow 0.2s;

	&:hover {
		box-shadow: ${({ theme }) => theme.shadow.depth02};
	}
`;

const CreatorRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 10px 12px 8px;
	overflow: hidden;
	cursor: pointer;
	border-radius: 6px;

	&:hover {
		background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
	}
`;

const CreatorAvatar = styled.div`
	width: 24px;
	height: 24px;
	border-radius: 50%;
	overflow: hidden;
	flex-shrink: 0;
`;

const CreatorName = styled.span`
	${({ theme }) => theme.typo.body.span_bold};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
	flex: 1;
`;

const ThumbnailWrapper = styled.div`
	width: 100%;
	height: 200px;
	overflow: hidden;
	position: relative;
`;

const RankBadge = styled.div<{ $isTop?: boolean }>`
	position: absolute;
	top: 8px;
	left: 8px;
	padding: 2px 8px;
	border-radius: 4px;
	background-color: ${({ $isTop, theme }) =>
		$isTop ? theme.colors.uplus.primary50_key : 'rgba(0, 0, 0, 0.6)'};
	color: #fff;
	font-size: 11px;
	font-weight: 700;
	line-height: 1.4;
	z-index: 1;
`;


const CardInfo = styled.div`
	padding: 10px 12px 12px;
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const CardDescription = styled.div`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	min-height: 32px;
`;

const CardDate = styled.div`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray50};
`;

const EmptyMessage = styled.div`
	${({ theme }) => theme.typo.body.p};
	color: ${({ theme }) => theme.colors.gray.gray50};
	text-align: center;
	padding: 40px 0;
`;

const { Grid } = style;

export default TrendHighlights;
